// @flow

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import StyledPhoneInput from '../SharedComponents/StyledPhoneInput';
import Recaptcha from 'react-recaptcha';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import Logo from '../../assets/askneo-favicon.png';
import AgreeToTOCLine from '../SharedComponents/AgreeToTOCLine';
import { useUserAuth } from '../Auth/UserSession';
import useSignupSubmission from '../../hooks/useSignupSubmission';

import { log, REGEX_PATTERNS } from '../../utils/jsUtils';
import SPBackend from '../../services/SPBackend';

import signupStyles from './Styles/Signup.Style';
import { createClasses } from '../../utils/stylesUtils';
import { useTheme } from '@mui/material/styles';

type Props = {};

const handleCaptchaLoaded = () => {
  log('[Captcha][handleCaptchaLoaded] captcha loaded');
};

const Signup = (props: Props) => {
  const { register, handleSubmit, errors, control } = useForm();
  const { setEmail, setPassword } = useUserAuth();

  const {
    couponValidation,
    couponCode,
    isSignupEnabled,
    canShowForm,
    trackFieldFocus,
    submit
  } = useSignupSubmission();

  const [isVerified, setIsVerified] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const onFieldChange = () => {
    setRegistrationError('');
  };

  const handleCaptcha = (captchaResponse) => {
    if (captchaResponse) {
      log('[Captcha][handleCaptcha] captchaResponse', captchaResponse);
      const captchaUserResponsePayload = {
        response: captchaResponse
      };
      SPBackend.verifyCaptcha(captchaUserResponsePayload)
        .then((response) => {
          if (response.data.success) {
            setIsVerified(true);
          } else {
            log('[Signup] verifyCaptcha not success:', response);
          }
        })
        .catch((err) => {
          log('[Signup] verifyCaptcha error:', err);
        });
    }
  };

  const createAccount = async (registration) => {
    log('[createAccount] registration:', registration);

    if (!agreedToTerms) {
      setRegistrationError('You must agree to the Terms & Conditions and Privacy Policy to create an account.');
      return;
    }

    if (!isVerified) {
      setRegistrationError('Please complete the captcha verification.');
      return;
    }

    setRegistrationError('');
    setRegistrationLoading(true);
    try {
      await submit({
        fullName: registration.fullName,
        email: registration.email,
        password: registration.password,
        phone: registration.phone,
        companyName: registration.companyName,
        agreed: agreedToTerms
      });
      // On success the hook navigates to /verifyPhone — this component unmounts,
      // so we deliberately do NOT touch state below this line.
    } catch (e) {
      // hook already dispatched a snack on failure; release the loading state.
      setRegistrationLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const theme = useTheme();
  const classes = createClasses(signupStyles, theme);

  return (
    <Grid container style={classes.contentCtr}>
      <Helmet>
        <title>Neo | Signup</title>
      </Helmet>

      <Grid item xs={12} style={classes.headerLogo}>
        <a href="/">
          <img height={40} src={Logo} alt="AskNeo Logo" />
        </a>
      </Grid>

      <Grid item xs={12}>
        <Typography style={classes.formTitle} variant="h4">
          {'Start your free 7-day trial'}
        </Typography>
        <Typography style={classes.formSubtitle} variant="body1">
          {'Start closing more deals with our 2-way texting and calling CRM.'}
        </Typography>

        {(() => {
          if (couponValidation.loading) {
            return (
              <Box sx={{
                textAlign: 'center',
                padding: 4,
                backgroundColor: '#f0f8ff',
                borderRadius: 2,
                marginTop: 2
              }}>
                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
                  validating signup link...
                </Typography>
                <Typography variant="body1">
                  Please wait while we verify your signup link.
                </Typography>
              </Box>
            );
          }

          if (couponCode && couponValidation.valid === false) {
            return (
              <Box sx={{
                textAlign: 'center',
                padding: 4,
                backgroundColor: '#ffebee',
                borderRadius: 2,
                marginTop: 2
              }}>
                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600, color: '#d32f2f' }}>
                  Invalid Code
                </Typography>
                <Typography variant="body1">
                  Please contact us at{' '}
                  <a href="mailto:hi@askneo.io" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                    hi@askneo.io
                  </a>
                  {' '}for assistance.
                </Typography>
                <Box sx={{ marginTop: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <a href="/login" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                      Sign in here
                    </a>
                  </Typography>
                </Box>
              </Box>
            );
          }

          if (!canShowForm) {
            return (
              <Box sx={{
                textAlign: 'center',
                padding: 4,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                marginTop: 2
              }}>
                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
                  New Signups Temporarily Paused
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: 2 }}>
                  We are currently accepting new members by invitation only to ensure the best experience for our community.
                </Typography>
                <Typography variant="body1">
                  If you'd like to join Askneo, please contact us at{' '}
                  <a href="mailto:hi@askneo.io" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                    hi@askneo.io
                  </a>
                  {' '}for more information.
                </Typography>
                <Box sx={{ marginTop: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <a href="/login" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                      Sign in here
                    </a>
                  </Typography>
                </Box>
              </Box>
            );
          }

          return null;
        })()}

        {(isSignupEnabled || couponValidation.valid === true) && !couponValidation.loading && (
          <form onSubmit={handleSubmit(createAccount)}>
            {registrationError !== '' && (
              <Typography style={classes.formErrorResponse} variant="body1">
                {registrationError}
              </Typography>
            )}

            <TextField
              fullWidth
              variant="outlined"
              label="Full Name"
              name="fullName"
              type="text"
              autoComplete="name"
              onChange={onFieldChange}
              onFocus={() => trackFieldFocus('fullName')}
              inputRef={register({
                required: true,
                validate: (v) => ((v || '').trim().length > 0) || 'required'
              })}
              sx={{
                width: '100%',
                margin: '4px 0px',
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.spacing(0.5)
                },
                '& input': {
                  paddingTop: '1.5rem',
                  paddingBottom: '1.5rem',
                  textAlign: 'left',
                  margin: 0,
                  border: 0
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'hsl(0,0%,20%)',
                  background: 'white',
                  padding: '0 5px',
                  fontSize: '1rem',
                  '&.MuiInputLabel-shrink': {
                    color: theme.palette.primary.dark
                  }
                }
              }}
            />
            <div style={classes.errorLabel}>
              {errors.fullName && 'Full name is required.'}
            </div>

            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              onChange={(event) => {
                setEmail(event.target.value);
                onFieldChange();
              }}
              onFocus={() => trackFieldFocus('email')}
              inputRef={register({ required: true, pattern: REGEX_PATTERNS.EMAIL })}
              sx={{
                width: '100%',
                borderRadius: 4,
                margin: '4px 0px',
                backgroundColor: 'white',
                '& input': {
                  paddingTop: '1.5rem',
                  paddingBottom: '1.5rem',
                  textAlign: 'left',
                  margin: 0,
                  border: 0
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.spacing(0.5)
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'hsl(0,0%,20%)',
                  background: 'white',
                  padding: '0 5px',
                  fontSize: '1rem',
                  '&.MuiInputLabel-shrink': {
                    color: theme.palette.primary.dark
                  }
                }
              }}
            />
            <div style={classes.errorLabel}>
              {errors.email && 'Valid Email is required.'}
            </div>

            <TextField
              fullWidth
              variant="outlined"
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              onChange={(event) => {
                setPassword(event.target.value);
                onFieldChange();
              }}
              onFocus={() => trackFieldFocus('password')}
              inputRef={register({ required: true, minLength: 6 })}
              sx={{
                width: '100%',
                borderRadius: 4,
                margin: '4px 0px',
                backgroundColor: 'white',
                '& input': {
                  paddingTop: '1.5rem',
                  paddingBottom: '1.5rem',
                  textAlign: 'left',
                  margin: 0,
                  border: 0
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.spacing(0.5)
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'hsl(0,0%,20%)',
                  background: 'white',
                  padding: '0 5px',
                  fontSize: '1rem',
                  '&.MuiInputLabel-shrink': {
                    color: theme.palette.primary.dark
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <div style={classes.errorLabel}>
              {errors.password && errors.password.type === 'required' && 'Password is required.'}
              {errors.password && errors.password.type === 'minLength' && 'Password length must be at least 6 digits or characters.'}
            </div>

            <Box sx={{ margin: '4px 0px', width: '100%' }}>
              <Controller
                control={control}
                name="phone"
                defaultValue=""
                rules={{ required: true }}
                render={({ onChange, value }) => (
                  <StyledPhoneInput
                    variant="outlined"
                    defaultCountry="US"
                    autoComplete="tel"
                    onChange={(val) => {
                      onChange(val);
                      onFieldChange();
                    }}
                    onFocus={() => trackFieldFocus('phone')}
                    placeholder="Mobile Phone number"
                    value={value}
                  />
                )}
              />
            </Box>
            <div style={classes.errorLabel}>
              {errors.phone && 'Mobile number is required.'}
            </div>

            <TextField
              fullWidth
              variant="outlined"
              label="Company Website"
              name="companyName"
              type="text"
              autoComplete="organization"
              onChange={onFieldChange}
              onFocus={() => trackFieldFocus('companyName')}
              inputRef={register({ required: true })}
              sx={{
                width: '100%',
                borderRadius: 4,
                margin: '4px 0px',
                backgroundColor: 'white',
                '& input': {
                  paddingTop: '1.5rem',
                  paddingBottom: '1.5rem',
                  textAlign: 'left',
                  margin: 0,
                  border: 0
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.spacing(0.5)
                }
              }}
              InputLabelProps={{
                sx: {
                  color: 'hsl(0,0%,20%)',
                  background: 'white',
                  padding: '0 5px',
                  fontSize: '1rem',
                  '&.MuiInputLabel-shrink': {
                    color: theme.palette.primary.dark
                  }
                }
              }}
            />
            <div style={classes.errorLabel}>
              {errors.companyName && 'Company website is required.'}
            </div>

            <div style={classes.captchaCtr}>
              <Recaptcha
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}
                render="explicit"
                verifyCallback={handleCaptcha}
                onloadCallback={handleCaptchaLoaded}
              />
            </div>

            <div style={classes.checkboxCtr}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  style={{ marginRight: '8px' }}
                  required
                />
                <AgreeToTOCLine />
              </label>
            </div>

            {registrationLoading ? (
              <Button
                aria-describedby="trial-reassurance"
                color="primary"
                disabled
                size="large"
                style={classes.signupBtn}
                variant="contained"
              >
                {'Starting your trial...'}
              </Button>
            ) : (
              <Button
                aria-describedby="trial-reassurance"
                color="primary"
                size="large"
                style={classes.signupBtn}
                type="submit"
                variant="contained"
              >
                {'Start my free trial'}
              </Button>
            )}
            <Typography id="trial-reassurance" style={classes.trialReassurance} variant="body2">
              {'7 Day Free Trial. Cancel anytime.'}
            </Typography>
            <Typography style={classes.signInPrompt} variant="body2">
              {'Already have an account? '}
              <Link to="/login" style={classes.signInLink}>
                {'Sign in here'}
              </Link>
            </Typography>
          </form>
        )}
      </Grid>

    </Grid>
  );
};

export default Signup;
